export const careManagement = {
  dashboard: {
    title: 'RPM Dashboard',
    refreshTooltip: 'Refreshing RPM dashboard data will update and re-calculate the data, which may take some time to complete.',
    noDataMessage: 'No data',
    fetchErrorMessage: 'Failed to retrieve data',
    sections: {
      enrollment: 'Enrollment',
      readings: 'Readings',
      devices: 'Devices',
      alertsAndTimeSpent: 'Alerts and Time Spent'
    },
    kpis: {
      patientsEnrolled: 'No of Patients Enrolled',
      patientsScheduledForEnrollment: 'No of Patients Scheduled For Enrollments',
      patientsCandidates: 'No of Patients Candidates',
      patientsActive: 'No of Patients Active (with readings)',
      patientsInactive: 'No of Patients Inactive (no readings)',
      patientsWithReadingsToday: 'No of Patients  with new readings received today',
      devicesSupplied: 'No of Devices supplied',
      unreadAlerts: 'No of unread Alerts'
    },
    charts: {
      enrollmentBreakdownTitle: 'No of Patients Candidates vs Scheduled For Enrollments vs Enrolled',
      readingsBreakdownTitle: 'No of Patients w/ of number of days with Readings',
      deviceTypeBreakdownTitle: 'No of Patients w/ BPM, BGM, and other devices',
      timeSpentBreakdownTitle: 'Patients with Time Spent',
      enrolledLabel: 'Enrolled',
      candidatesLabel: 'Candidates',
      scheduledForEnrollmentLabel: 'Scheduled For Enrollments'
    }
  },
  patients: {
    title: 'Patients',
    // The patient grid doubles as the pharmacists' work queue.
    queueTitle: 'Queue',
    relatedPages: {
      conversations: 'Convos',
      medHistory: 'Med History',
      devices: 'Devices',
      sharedReports: 'Shared Reports'
    },
    actions: {
      followUpTasks: 'My Tasks: Follow-up',
      onboardPatients: 'Onboard',
      change: 'Change',
      rpmReports: 'RPM Reports',
      scheduleForLater: 'Schedule for Later',
      sendNow: 'Send Now'
    },
    tabs: {
      candidates: 'Candidates',
      priorityCandidates: 'Priority Candidates',
      priorityCandidatesLines: ['Priority', 'Candidates'],
      scheduledForEnrollment: 'Scheduled for Enrollment',
      scheduledForEnrollmentLines: ['Scheduled', 'For Enrollment'],
      enrolled: 'Enrolled',
      unenrolled: 'Unenrolled'
    },
    filters: {
      followUpVisitsLabel: 'Follow-up Visits:',
      monthlyTasksDueLabel: 'Monthly Tasks Due:',
      billingLabel: 'Billing:',
      refreshTooltip: 'Refreshing enrolled accounts will update and re-calculate the data, which may take some time to complete.',
      refreshDisabledTooltip: 'You cannot refresh accounts that are not in the current month.',
      updatedLessThanAMinuteAgo: (time: string) => `Updated less than 1 minute ago (${time})`,
      updatedMinutesAgo: (minutes: number, time: string) => `Updated ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago (${time})`,
      updatedHoursAgo: (hours: number, time: string) => `Updated ${hours} ${hours === 1 ? 'hour' : 'hours'} ago (${time})`,
      updatedOn: (dateTime: string) => `Updated ${dateTime}`,
      lastFullRefresh: (dateTime: string) => `Last full refresh: ${dateTime}`,
      noFullRefreshThisMonth: 'No full refresh this month',
      priorityThresholdPrefix: 'Showing candidates with score',
      priorityThresholdSuffix: 'and above'
    },
    dialogs: {
      confirmScheduleTitle: 'Confirm Submit RPM Reports as Ready',
      confirmScheduleMessage: (monthAndYear: string) =>
        `This will submit the RPM reports for the selected patients for the month of ${monthAndYear} as ready to send. Are you sure you want to proceed?`,
      confirmSendNowTitle: 'Confirm Instantly Submit RPM Reports For Review',
      confirmSendNowMessage: (monthAndYear: string) =>
        `This will <b>instantly send</b> the RPM reports related to selected patients for the month of ${monthAndYear}. Are you sure you want to proceed?`,
      submitFailedTitle: 'Failed to Submit Patients RPM Reports',
      submitSuccessMessage: 'Patients RPM reports submitted successfully',
      submitFailedMessage: 'Failed to submit patients RPM reports'
    },
    kpi: {
      hideLabel: 'Hide Patient KPI',
      viewLabel: 'View Patient KPI',
      extraKpisToggle: 'Readings Today & Shared Reports',
      extraKpisHint: 'Show or hide the Readings Today and Shared Reports cards. They appear only where the screen has room for them',
      scheduledAppointments: {
        title: 'Scheduled Appointments',
        today: 'Today',
        thisWeek: 'This Week',
        todayAria: (count: number) => `${count} appointments scheduled today. Open Patient Schedules`,
        thisWeekAria: (count: number) => `${count} appointments scheduled this week, Monday to Saturday. Open Patient Schedules`,
        hint: 'Monday to Saturday. Click to open Patient Schedules'
      },
      careManagementTracker: {
        title: 'Care Management Tracker',
        totalOption: 'Total',
        byProgramOption: 'By Program',
        emptyState: 'Per-program breakdown appears once RPM/CCM time data is available'
      },
      deviceReadingsTracker: {
        title: 'Device Readings Tracker',
        allOption: 'All',
        bpOption: 'BP',
        bgOption: 'BG',
        devicesUnavailable: "Patients' devices are unavailable right now",
        deviceViewSummary: (count: number, device: string) => `${count} patients with a ${device} device · counts all their readings`,
        deviceViewDetail: (device: string, alsoOther: number, other: string) =>
          `Patients who have a ${device} device, placed by their readings this month from every device they have, not only the ${device} one.` +
          (alsoOther > 0
            ? ` ${alsoOther} of them also ${alsoOther === 1 ? 'has' : 'have'} a ${other} device and ${
                alsoOther === 1 ? 'appears' : 'appear'
              } in the ${other} view too.`
            : ` None of them also has a ${other} device.`)
      },
      enrollmentsPanel: {
        // "(all time)" because the card sits under a month header that scopes everything else
        // below it -- this one figure is the exception and has to say so.
        title: 'Enrollments (all time)',
        totalEnrolled: 'Patients',
        bp: 'BP',
        bg: 'BG',
        periodHeader: 'Period',
        allHeader: 'All',
        rpmChip: 'RPM',
        ccmChip: 'CCM',
        thisMonth: 'This Month',
        yesterday: 'Yesterday'
      },
      sharedReports: {
        title: 'Shared Reports',
        // getRpmReportsCounts counts every report the pharmacy has had, not this month's.
        scope: 'All time',
        hint: 'Reports sent to providers, all time. Click to open Shared Reports, or click a count to open that tab',
        openAria: 'Open Shared Reports',
        ready: 'Ready',
        scheduled: 'Scheduled',
        sent: 'Sent',
        failed: 'Failed',
        tileAria: (count: number, status: string) => `${count} reports ${status}. Open Shared Reports on the ${status} tab`,
        unavailable: 'Report counts are unavailable right now'
      },
      readingsToday: {
        title: 'Readings Today',
        hint: 'Patients who sent at least one reading today, by device. Click the total or a bar to list them below',
        patients: 'Patients',
        ofPatients: (enrolled: number) => `of ${enrolled} patients`,
        bp: 'BP',
        bg: 'BG',
        hr: 'HR',
        updatedAt: (time: string) => `Updated ${time}`,
        refresh: 'Refresh readings today',
        filterLabel: 'Readings today',
        deviceFilterLabel: (device: string) => `${device} reading today`,
        allAria: (count: number) => `${count} patients with a reading today. Show them in the list`,
        unavailable: 'Readings are unavailable right now'
      }
    },
    priorityLevel: {
      highLabel: (mediumHigh: number) => `( ${mediumHigh} <= score )`,
      mediumLabel: (lowMedium: number, mediumHigh: number) => `( ${lowMedium}  <= score < ${mediumHigh})`,
      lowLabel: (lowMedium: number) => `( score < ${lowMedium} )`
    },
    grid: {
      columns: {
        nameLastFirst: 'Name (Last, First)',
        phoneNumber: 'Phone number',
        priorityLevel: 'Priority Level',
        dob: 'DOB',
        supportingData: 'Supporting Data',
        dmLikelihood: 'DM Likelihood',
        htnLikelihood: 'HTN Likelihood',
        zipcode: 'Zipcode',
        devices: 'Devices',
        lastReadingOn: 'Last Reading On',
        lastReading: 'Last Reading',
        dateEnrolled: 'Date Enrolled',
        lastMedRecOn: 'Last MedRec On',
        initialVisitOn: 'New Patient Visit On',
        followUpVisit: 'Follow-up Visit',
        providerApprovals: 'Provider Approvals',
        lastContactedAt: 'Last Contacted At',
        lastActivityAt: 'Last Activity At',
        monthlyTotalReadings: 'Monthly Total Readings',
        totalTime: 'Total Time',
        rpmTime: 'RPM Time',
        ccmTime: 'CCM Time',
        program: 'Program',
        enrollmentMode: 'Enrollment Mode',
        lastLogBp: 'Last Log BP',
        lastLogBg: 'Last Log BG',
        lastLogHr: 'Last Log HR',
        report: 'Report',
        actions: 'Actions'
      },
      tooltips: {
        unsnoozePatient: 'Unsnooze patient',
        snoozePatient: 'Snooze patient',
        tagPatient: 'Tag Patient',
        recalculateScore: 'Recalculate Score',
        supportingDataInfo: 'View more data such as diagnoses data, medsync data, and claims data',
        scheduleAVisit: 'Schedule a visit',
        lastVisitLabel: 'Last',
        asyncLabel: '(Async)',
        visitLabelSuffix: 'Visit:',
        yearSinceEnrollmentMessage: 'This patient has not completed a year since enrollment',
        viewRpmSummaryReport: 'View RPM Summary Report',
        editSchedule: 'Edit Schedule',
        noInitialVisitScheduled: 'No New Patient Visit scheduled',
        cancelScheduleForEnrollment: 'Cancel Schedule for Enrollment',
        scheduleForEnrollment: 'Schedule for Enrollment',
        noContactInfo: 'No contact info'
      },
      chips: {
        rpm: 'RPM',
        ccm: 'CCM',
        zeroSeconds: '0s'
      },
      emptyValues: {
        notAvailable: 'N/A',
        none: 'None',
        unknown: 'Unknown'
      },
      dialogs: {
        supportingDataTitle: 'Supporting Data',
        confirmationRequiredTitle: 'Confirmation Required',
        snoozeAction: 'Snooze ',
        unsnoozeAction: 'Unsnooze ',
        scheduleForEnrollmentTitle: 'Schedule for Enrollment',
        deleteAllInitialVisitFollowups: 'Delete All New Patient Visit Follow-ups',
        deleteInitialVisitFollowup: 'Delete New Patient Visit Follow-up',
        scheduleFollowUpVisitTitle: 'Schedule Follow-up Visit',
        rpmReportPreviewTitle: 'RPM Report Preview'
      },
      snackbars: {
        noInitialVisitAppointmentFound: 'No New Patient Visit appointment found for this patient',
        noData: 'No data',
        failedToGetUnenrolledCount: 'Failed to get Unenrolled Patients count',
        failedToSnoozePriorityCandidates: 'Failed to snooze priority candidates',
        scoreUnavailable: 'Score unavailable, try again later',
        priorityScoreUnchanged: 'Priority score unchanged',
        unableToRetrievePatientData: 'Unable to retrieve patient data'
      },
      mobileList: {
        noPatientsFound: 'No patients found',
        viewPatientDetails: 'View patient details',
        noReadingsYet: 'No readings yet'
      }
    },
    batchScore: {
      recalculating: 'Recalculating…',
      recalcScores: 'Recalc Scores',
      statusFetchError: 'Unable to fetch recalculation status',
      recalculatedWithFailures: (failed: number) => `Priority scores recalculated (${failed} failed)`,
      recalculated: 'Priority scores recalculated',
      batchFailedDefault: 'Batch recalculation failed',
      startFailedDefault: 'Unable to start recalculation',
      startFailedRetry: 'Unable to start recalculation, try again later'
    },
    csvExport: {
      export: 'Export',
      downloadExports: 'Download Exports',
      reportFallbackName: 'Report',
      generateReportFailed: (error: string) => `Failed to generate report: ${error}`
    },
    priorityFilters: {
      menuLabel: 'Filters',
      firstName: 'First Name',
      lastName: 'Last Name',
      insuranceFiltersButton: 'Insurance Filters',
      advancedInsuranceFiltersTitle: 'Advanced Insurance Filters',
      searchPlaceholder: 'Search Priority Patients (by First Name and Last Name)',
      diabetesHighLikelihood: 'Diabetes: High Likelihood',
      hypertensionHighLikelihood: 'Hypertension: High Likelihood'
    },
    insuranceFilters: {
      title: 'Insurance Payers',
      selectAll: 'Select All',
      clearAll: 'Clear All',
      noCategoriesAvailable: 'No insurance plan categories available',
      tabsAriaLabel: 'insurance plan categories',
      noPayersAvailable: (categoryName: string) => `No payers available for ${categoryName}`,
      applyFilters: (count: number) => `Apply Filters (${count} ${count === 1 ? 'payer' : 'payers'} selected)`
    }
  }
} as const;
